import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Send, Smile, X, Heart, Crown, Mail, ArrowLeft, Users, Bell, BellOff, MessageSquareQuote, ImageIcon, Trophy, Hand, Zap } from "lucide-react";
import { toast } from "sonner";
import RadiojarPlayer from "@/components/RadiojarPlayer";
import ChatVoteBanner from "@/components/ChatVoteBanner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useNowPlaying from "@/hooks/useNowPlaying";
import { getOrCreateSessionId } from "@/lib/visitorSession";
import { playWhisperPing, isWhisperSoundMuted, setWhisperSoundMuted } from "@/lib/whisperSound";
import { subscribe as subscribeAudio } from "@/lib/audioAnalyzer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "blackwire_chat_session";

export default function ChatRoom() {
  const [session, setSession] = useState(null); // {user_id, nickname}
  const [nicknameInput, setNicknameInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [online, setOnline] = useState(0);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderExhausted, setOlderExhausted] = useState(false);
  // Voter of the Week — used to render a 🔥 crown next to matching nicknames.
  const [voterOfTheWeek, setVoterOfTheWeek] = useState(""); // lowercased nickname
  // Online listener list — fetched on demand when the popover opens.
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineUsersOpen, setOnlineUsersOpen] = useState(false);
  // Whisper state. peer={user_id, nickname} when actively in a whisper view.
  const [whisperPeer, setWhisperPeer] = useState(null);
  const [whispers, setWhispers] = useState([]); // all whispers sent or received
  const [whisperDraft, setWhisperDraft] = useState("");
  const [sendingWhisper, setSendingWhisper] = useState(false);
  const [soundMuted, setSoundMuted] = useState(() => isWhisperSoundMuted());
  const whispersLastSeenTsRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  // Sound-reactive ref for the "THE PIT" title — mutated 60 fps by the
  // shared audio analyser when the Radiojar player is streaming.
  const pitTitleRef = useRef(null);
  const lastTsRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const mountedRef = useRef(true);
  // Mirror of `session` for use inside fetchMessages (which has [] deps so it
  // doesn't recreate on every session change).
  const sessionRef = useRef(null);
  // Becomes true after the first successful fetchMessages run, so mentions in
  // pre-existing chat history don't fire a chime/toast on page load.
  const messagesFirstFetchDoneRef = useRef(false);
  // Track whether the user is currently pinned to the bottom of the chat.
  // We only auto-scroll on new messages if they were already at the bottom,
  // so reading older messages doesn't get interrupted by incoming chatter.
  const atBottomRef = useRef(true);
  // Forced scroll request (set true when the local user sends their own msg).
  const forceScrollRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Sound-reactive "THE PIT" title — subscribe to the shared audio analyser
  // and pulse the header text in time with the stream's bass energy.
  // Runs on the same rAF loop as any other visualizer for zero overhead.
  useEffect(() => {
    let lastBass = 0;
    const unsub = subscribeAudio((bass) => {
      const smoothed = bass > lastBass ? bass : lastBass * 0.85;
      lastBass = smoothed;
      if (!pitTitleRef.current) return;
      const scale = 1 + smoothed * 0.09;
      const glow = 4 + smoothed * 18;
      const alpha = 0.25 + smoothed * 0.75;
      pitTitleRef.current.style.transform = `scale(${scale.toFixed(3)})`;
      pitTitleRef.current.style.textShadow = `0 0 ${glow.toFixed(1)}px rgba(220,38,38,${alpha.toFixed(3)})`;
    });
    return unsub;
  }, []);

  // Restore session
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        setSession(JSON.parse(raw));
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Keep sessionRef in sync with session state for use inside callbacks that
  // can't depend on `session` without recreating polling intervals.
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const fetchMessages = useCallback(async () => {
    try {
      // Initial load: pull only the most recent 25 messages so the chat is
      // immediately scrollable to the latest. After that we keep polling
      // for NEW messages via the `since` cursor and never auto-fetch older
      // history — the user pulls older messages on demand via the button.
      const url = lastTsRef.current
        ? `${API}/chat/messages?since=${encodeURIComponent(lastTsRef.current)}`
        : `${API}/chat/messages?limit=25`;
      const res = await axios.get(url, { timeout: 12000 });
      if (!mountedRef.current) return true;
      const incoming = res.data.filter((m) => !seenIdsRef.current.has(m.id));
      if (incoming.length === 0) {
        // Mark the first poll done even if it returned nothing, so a later
        // single-message poll with a mention isn't suppressed as "history".
        messagesFirstFetchDoneRef.current = true;
        return true;
      }
      incoming.forEach((m) => seenIdsRef.current.add(m.id));
      setMessages((prev) => {
        const merged = [...prev, ...incoming];
        // Keep last 200 IN MEMORY (the user can still load further back via the
        // "Older" button, which prepends new chunks).
        const trimmed = merged.slice(-200);
        if (trimmed.length < merged.length) {
          const keep = new Set(trimmed.map((m) => m.id));
          seenIdsRef.current = keep;
        }
        return trimmed;
      });
      const lastTs = incoming[incoming.length - 1].timestamp;
      if (!lastTsRef.current || lastTs > lastTsRef.current) {
        lastTsRef.current = lastTs;
      }

      // @mention detection: if any brand-new incoming message contains
      // @<currentNickname> and isn't from the current user themselves, fire
      // a chime + toast. Skip the very first fetch — those are pre-existing
      // messages already on the page when the user joined.
      if (messagesFirstFetchDoneRef.current) {
        const me = sessionRef.current;
        const myNick = me?.nickname?.trim();
        if (myNick) {
          const myNickLower = myNick.toLowerCase();
          // Same word-boundary rule as the renderer: @ preceded by start or a
          // non-word char, then alnum/underscore nickname. Case-insensitive.
          const mentionRegex = new RegExp(
            `(^|[^A-Za-z0-9_])@${myNickLower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![A-Za-z0-9_])`,
            "i"
          );
          const mentioner = incoming.find(
            (m) =>
              m.user_id !== me.user_id &&
              typeof m.message === "string" &&
              mentionRegex.test(m.message)
          );
          if (mentioner) {
            toast(`@${mentioner.nickname} mentioned you in the pit`);
            playWhisperPing();
          }
        }
      }
      messagesFirstFetchDoneRef.current = true;
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // Load the previous 25 messages BEFORE the oldest one currently on screen.
  // Prepends them to the list and preserves the user's scroll position so
  // they keep reading where they were.
  const loadOlderMessages = useCallback(async () => {
    if (loadingOlder || olderExhausted) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoadingOlder(true);
    const el = listRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;
    const prevScrollTop = el ? el.scrollTop : 0;
    try {
      const res = await axios.get(
        `${API}/chat/messages?before=${encodeURIComponent(oldest.timestamp)}&limit=25`
      );
      const older = (res.data || []).filter((m) => !seenIdsRef.current.has(m.id));
      if (older.length === 0) {
        setOlderExhausted(true);
        return;
      }
      older.forEach((m) => seenIdsRef.current.add(m.id));
      setMessages((prev) => [...older, ...prev]);
      // After React paints the prepended chunk, restore scroll position so
      // the messages the user was reading don't jump.
      requestAnimationFrame(() => {
        if (!listRef.current) return;
        const newScrollHeight = listRef.current.scrollHeight;
        listRef.current.scrollTop =
          prevScrollTop + (newScrollHeight - prevScrollHeight);
      });
    } catch (e) {
      toast.error("Couldn't load older messages");
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, olderExhausted, messages]);

  const fetchOnline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/online`, { timeout: 10000 });
      setOnline(res.data.count);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/online-users`);
      if (mountedRef.current) setOnlineUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      /* silent */
    }
  }, []);

  const fetchVoterOfTheWeek = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/voter-of-the-week`);
      if (res?.data?.ok && res.data.nickname) {
        setVoterOfTheWeek(String(res.data.nickname).toLowerCase());
      } else {
        setVoterOfTheWeek("");
      }
    } catch (e) {
      /* silent */
    }
  }, []);

  const fetchWhispers = useCallback(async () => {
    if (!session?.user_id) return true;
    try {
      const params = { user_id: session.user_id };
      const isFirstFetch = !whispersLastSeenTsRef.current;
      if (whispersLastSeenTsRef.current) {
        params.since = whispersLastSeenTsRef.current;
      }
      const res = await axios.get(`${API}/chat/whispers`, { params, timeout: 10000 });
      const incoming = Array.isArray(res.data) ? res.data : [];
      if (!incoming.length) return true;
      // Track the latest timestamp we've seen so subsequent polls are
      // incremental.
      const latestTs = incoming[0]?.timestamp;
      if (latestTs && latestTs > (whispersLastSeenTsRef.current || "")) {
        whispersLastSeenTsRef.current = latestTs;
      }
      setWhispers((prev) => {
        const byId = new Map(prev.map((w) => [w.id, w]));
        for (const w of incoming) byId.set(w.id, w);
        // Newest first → return ascending so threads render naturally.
        return Array.from(byId.values()).sort((a, b) =>
          a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
        );
      });
      // Toast (and chime) on brand-new incoming whispers (not own sends,
      // not already seen, not currently in their thread). Skip on the
      // first fetch of the session — those are pre-existing unread, the
      // unread badge already tells you they're waiting.
      if (isFirstFetch) return true;
      for (const w of incoming) {
        if (
          w.to_user_id === session.user_id &&
          !w.read_by_recipient &&
          (!whisperPeer || whisperPeer.user_id !== w.from_user_id)
        ) {
          toast(`✉ Whisper from @${w.from_nickname}`);
          playWhisperPing();
          break; // one toast + chime per poll is enough
        }
      }
      return true;
    } catch (e) {
      return false;
    }
  }, [session, whisperPeer]);

  // Adaptive polling: normal interval on success, exponential backoff on
  // failures so a brief backend/ingress hiccup doesn't hammer the server or
  // spam "no connection" toasts. Handles 50+ concurrent chatters cleanly by
  // reducing polling load ~40% vs the old 2.5s hard interval.
  //   Messages:  4s baseline → up to 30s if network flaking
  //   Online:    12s baseline → up to 60s if network flaking
  //   Voter:     60s baseline (very light, no backoff needed)
  useEffect(() => {
    fetchMessages();
    fetchOnline();
    fetchVoterOfTheWeek();

    let messagesTimer = null;
    let onlineTimer = null;
    let messagesFails = 0;
    let onlineFails = 0;

    const nextDelay = (baseMs, failCount, maxMs) =>
      Math.min(baseMs * Math.pow(2, Math.min(failCount, 3)), maxMs);

    const scheduleMessages = () => {
      const delay = nextDelay(4000, messagesFails, 30_000);
      messagesTimer = setTimeout(async () => {
        if (!mountedRef.current) return;
        const ok = await fetchMessages();
        messagesFails = ok ? 0 : messagesFails + 1;
        if (mountedRef.current) scheduleMessages();
      }, delay);
    };

    const scheduleOnline = () => {
      const delay = nextDelay(12_000, onlineFails, 60_000);
      onlineTimer = setTimeout(async () => {
        if (!mountedRef.current) return;
        const ok = await fetchOnline();
        onlineFails = ok ? 0 : onlineFails + 1;
        if (mountedRef.current) scheduleOnline();
      }, delay);
    };

    scheduleMessages();
    scheduleOnline();
    const votw = setInterval(fetchVoterOfTheWeek, 60_000);

    return () => {
      if (messagesTimer) clearTimeout(messagesTimer);
      if (onlineTimer) clearTimeout(onlineTimer);
      clearInterval(votw);
    };
  }, [fetchMessages, fetchOnline, fetchVoterOfTheWeek]);

  // Whispers poller — only runs once the user has joined chat (has a
  // user_id). Same adaptive backoff pattern as messages/online.
  useEffect(() => {
    if (!session?.user_id) return;
    fetchWhispers();

    let timer = null;
    let fails = 0;
    const nextDelay = (base, count, max) =>
      Math.min(base * Math.pow(2, Math.min(count, 3)), max);

    const schedule = () => {
      const delay = nextDelay(6000, fails, 45_000);
      timer = setTimeout(async () => {
        if (!mountedRef.current) return;
        const ok = await fetchWhispers();
        fails = ok ? 0 : fails + 1;
        if (mountedRef.current) schedule();
      }, delay);
    };
    schedule();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [session, fetchWhispers]);

  useEffect(() => {
    if (!session) return;
    const beat = async () => {
      try {
        await axios.post(`${API}/chat/heartbeat`, session);
      } catch (e) {
        /* silent */
      }
    };
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [session]);

  // Smart auto-scroll: only stick to bottom if the user is already there
  // (or sent a message themselves). Otherwise we leave them alone and bump
  // the unread counter so they can jump down when ready.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (forceScrollRef.current || atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      atBottomRef.current = true;
      forceScrollRef.current = false;
      setUnread(0);
    } else {
      // New messages arrived while the user is scrolled up. Count them so
      // the "new messages" pill can show how many they missed.
      setUnread((u) => Math.min(u + 1, 99));
    }
  }, [messages]);

  // Track scroll position to know if the user is pinned to the bottom.
  // We use a GENEROUS threshold (~600px) so casual scroll bumps or being a
  // message or two above the latest still counts as "at bottom" — auto-scroll
  // keeps working. Only if the user has deliberately scrolled way up (reading
  // history) do we pause auto-scroll and show the "↓ N new" pill.
  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = dist < 600;
    atBottomRef.current = atBottom;
    if (atBottom) setUnread(0);
  };

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setUnread(0);
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    const nick = nicknameInput.trim();
    if (!nick) return;
    try {
      const res = await axios.post(`${API}/chat/join`, { nickname: nick });
      const next = { user_id: res.data.user_id, nickname: res.data.nickname };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      if (!mountedRef.current) return;
      setSession(next);
      setNicknameInput("");
      toast.success(`Welcome, ${nick}. The pit is yours.`);
      // Pull fresh messages immediately so the join system line appears
      fetchMessages();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not join chat";
      toast.error(msg);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    // Belt-and-braces: never let a click silently do nothing.
    if (!session) {
      toast.error("Not signed into the chat — refresh the page and try again.");
      return;
    }
    if (sending) return;
    const txt = draft.trim();
    if (!txt) return;
    setSending(true);
    // One silent retry on transient network errors — covers brief ISP blips
    // (WiFi flicker, mobile handoff) so users don't see a scary toast for a
    // sub-second glitch that resolves on its own.
    const trySend = async (attempt) => {
      try {
        await axios.post(
          `${API}/chat/message`,
          { ...session, message: txt },
          { timeout: 15000 },
        );
      } catch (err) {
        const isTransient =
          err?.code === "ECONNABORTED" ||
          err?.code === "ERR_NETWORK" ||
          err?.message === "Network Error" ||
          (err?.response?.status >= 500 && err?.response?.status < 600);
        if (attempt === 0 && isTransient) {
          await new Promise((r) => setTimeout(r, 1500));
          return trySend(1);
        }
        throw err;
      }
    };
    try {
      await trySend(0);
      setDraft("");
      // User sent their own message — always scroll them to the bottom,
      // even if they were reading history above.
      forceScrollRef.current = true;
      fetchMessages();
    } catch (err) {
      // Surface a specific error for EVERY class of failure so silent
      // "click send, nothing happens" bugs can't hide anymore.
      console.error("[DRR] chat send failed", err);
      let msg = "Couldn't send — check your connection and try again.";
      if (err?.code === "ECONNABORTED") {
        msg = "Send timed out. Your network is slow — try again.";
      } else if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        msg = "No connection. An ad blocker, VPN, or firewall may be blocking the chat — try disabling and retry.";
      } else if (err?.response?.status === 400) {
        msg = err.response.data?.detail || "Message rejected — check nickname or content.";
      } else if (err?.response?.status === 429) {
        msg = "Sending too fast. Wait a beat.";
      } else if (err?.response?.status >= 500) {
        msg = "Server hiccup — try again in a moment.";
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Send a curated sticker straight into chat as `[img]url[/img]` — the
  // existing BBCode renderer turns that into an inline image with the same
  // modal preview as any other [img] tag. Skips the draft entirely so it
  // feels like a one-tap reaction button. Fires a fire-and-forget hit to
  // the usage counter so the admin can see which stickers are popular.
  const sendSticker = async (sticker) => {
    if (!session) {
      toast.error("Not signed into the chat — refresh the page and try again.");
      return;
    }
    if (sending || !sticker?.image_url) return;
    const msg = `[img]${sticker.image_url}[/img]`;
    setSending(true);
    try {
      await axios.post(
        `${API}/chat/message`,
        { ...session, message: msg },
        { timeout: 15000 },
      );
      forceScrollRef.current = true;
      fetchMessages();
      if (sticker.id) {
        // Don't await — counter is best-effort and shouldn't block UX.
        axios.post(`${API}/chat-stickers/${sticker.id}/use`).catch(() => {});
      }
    } catch (err) {
      console.error("[DRR] sticker send failed", err);
      let detail = "Couldn't send sticker — check your connection and try again.";
      if (err?.code === "ECONNABORTED") {
        detail = "Sticker send timed out. Try again.";
      } else if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        detail = "No connection. An ad blocker or firewall may be blocking chat.";
      } else if (err?.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  // --- Whisper helpers -----------------------------------------------------

  const openWhisper = useCallback(
    async (peer) => {
      // peer: {user_id, nickname}. If the user clicks their own name in the
      // online list or a message, this is a no-op.
      if (!session?.user_id) {
        toast.error("Join chat first to send whispers");
        return;
      }
      if (!peer || !peer.user_id || peer.user_id === session.user_id) return;
      setWhisperPeer(peer);
      setOnlineUsersOpen(false);
      // Mark anything from this peer as read so the inbox badge clears.
      try {
        await axios.post(`${API}/chat/whispers/mark-read`, {
          user_id: session.user_id,
          peer_user_id: peer.user_id,
        });
        // Optimistically flip read flags in our local copy too.
        setWhispers((prev) =>
          prev.map((w) =>
            w.from_user_id === peer.user_id && w.to_user_id === session.user_id
              ? { ...w, read_by_recipient: true }
              : w
          )
        );
      } catch (e) {
        /* silent */
      }
    },
    [session]
  );

  const closeWhisper = useCallback(() => setWhisperPeer(null), []);

  const handleSendWhisper = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!session?.user_id || !whisperPeer || sendingWhisper) return;
      const text = whisperDraft.trim();
      if (!text) return;
      setSendingWhisper(true);
      try {
        const res = await axios.post(`${API}/chat/whisper`, {
          from_user_id: session.user_id,
          to_user_id: whisperPeer.user_id,
          text,
        });
        const w = res?.data;
        if (w) {
          setWhispers((prev) => {
            if (prev.some((x) => x.id === w.id)) return prev;
            return [...prev, w].sort((a, b) =>
              a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
            );
          });
          if (w.timestamp > (whispersLastSeenTsRef.current || "")) {
            whispersLastSeenTsRef.current = w.timestamp;
          }
        }
        setWhisperDraft("");
      } catch (err) {
        const msg = err?.response?.data?.detail || "Could not send whisper";
        toast.error(msg);
      } finally {
        setSendingWhisper(false);
      }
    },
    [session, whisperPeer, whisperDraft, sendingWhisper]
  );

  // Whispers I've received and not yet read — used for the inbox badge.
  const unreadWhisperCount = (() => {
    if (!session?.user_id) return 0;
    return whispers.filter(
      (w) => w.to_user_id === session.user_id && !w.read_by_recipient
    ).length;
  })();

  // Subset of whispers shown in the active thread (most recent 100, ascending).
  const activeWhisperThread = (() => {
    if (!whisperPeer || !session?.user_id) return [];
    return whispers
      .filter(
        (w) =>
          (w.from_user_id === session.user_id && w.to_user_id === whisperPeer.user_id) ||
          (w.from_user_id === whisperPeer.user_id && w.to_user_id === session.user_id)
      )
      .slice(-100);
  })();

  // --- Typing indicators -----------------------------------------------------
  // Public chat: which other listeners are typing right now (nicknames)
  // Whisper:    is the active peer typing to us?
  const [publicTyping, setPublicTyping] = useState([]);
  const [peerTyping, setPeerTyping] = useState(false);
  const lastTypingPingRef = useRef(0);

  const pingTyping = useCallback(
    (peerUserId) => {
      if (!session?.user_id) return;
      // Debounce: never ping more than once every 2.5s for the same scope.
      const now = Date.now();
      if (now - lastTypingPingRef.current < 2500) return;
      lastTypingPingRef.current = now;
      axios
        .post(`${API}/chat/typing`, {
          user_id: session.user_id,
          peer_user_id: peerUserId || null,
        })
        .catch(() => {});
    },
    [session]
  );

  // Reset the debounce timer whenever the scope (public vs. whisper peer)
  // changes so the first ping in a new context fires immediately.
  useEffect(() => {
    lastTypingPingRef.current = 0;
  }, [whisperPeer]);

  // Poll typing indicators every 2.5s. Pulls public typers when in public
  // chat, peer typing when in a whisper view.
  useEffect(() => {
    if (!session?.user_id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        if (whisperPeer) {
          const res = await axios.get(`${API}/chat/typing`, {
            params: { user_id: session.user_id, peer_user_id: whisperPeer.user_id },
          });
          if (!cancelled) setPeerTyping(Array.isArray(res.data) && res.data.length > 0);
        } else {
          const res = await axios.get(`${API}/chat/typing`, {
            params: { user_id: session.user_id },
          });
          if (!cancelled) setPublicTyping(Array.isArray(res.data) ? res.data : []);
        }
      } catch (e) {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [session, whisperPeer]);

  // Delete one of your own messages (within the backend's 5-min window).
  // We optimistically remove it from the local list first, then call the
  // server. If the server says no (too old, not yours, etc.) we restore it.
  const handleDeleteMessage = async (msgId) => {
    if (!session) return;
    const prev = messages;
    setMessages((list) => list.filter((m) => m.id !== msgId));
    try {
      await axios.post(`${API}/chat/delete`, {
        user_id: session.user_id,
        message_id: msgId,
      });
    } catch (err) {
      // Roll back the optimistic delete and surface the error.
      setMessages(prev);
      const detail = err?.response?.data?.detail || "Could not delete message";
      toast.error(detail);
    }
  };

  // Insert an emoji at the current cursor position (or append to end).
  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) {
      setDraft((d) => (d + emoji).slice(0, 500));
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = (draft.slice(0, start) + emoji + draft.slice(end)).slice(0, 500);
    setDraft(next);
    // Restore focus + caret after React re-render
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      const pos = Math.min(start + emoji.length, next.length);
      try {
        inputRef.current.setSelectionRange(pos, pos);
      } catch (e) {
        /* some inputs don't support selection */
      }
    });
  };

  // "Vibe Check" — prefill the chat input with a brutalist-styled track
  // citation so listeners can shout out something they hear in the
  // Recently-Played dropdown. Caret lands at the end so they keep typing
  // their reaction. Quietly no-ops if the listener hasn't joined chat.
  const quoteTrack = (title, artist) => {
    if (!session) {
      toast("Join the pit first to shout out a track.");
      return;
    }
    if (!title) return;
    const cite = artist
      ? `\\m/ "${title}" by ${artist} — `
      : `\\m/ "${title}" — `;
    // Don't double-stamp a citation already at the start of the draft.
    const current = draft || "";
    const next = (current.startsWith(cite.trimEnd()) ? current : cite + current).slice(0, 500);
    setDraft(next);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      try {
        inputRef.current.setSelectionRange(next.length, next.length);
      } catch (e) {
        /* some inputs don't support selection */
      }
    });
  };

  return (
    <section
      id="chat"
      className="relative bg-zinc-950 scanlines h-full flex flex-col flex-1 min-h-0 md:min-h-[640px]"
      data-testid="chat-section"
    >
      {/* Chat header */}
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="on-air-dot" aria-hidden />
          <span
            ref={pitTitleRef}
            className="font-display text-xl tracking-tight origin-left transition-transform duration-75 ease-out will-change-transform"
            data-testid="chat-pit-title"
          >
            THE PIT
          </span>
          <span className="text-xs text-zinc-300 tracking-widest uppercase ml-2 hidden sm:inline">
            // live_chat
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Whisper inbox button — only shown once user has joined chat */}
          {session?.user_id && (
            <>
            <button
              type="button"
              onClick={() => {
                const next = !soundMuted;
                setSoundMuted(next);
                setWhisperSoundMuted(next);
                if (!next) {
                  // Preview-play when unmuting so the listener knows what
                  // to expect (also unlocks the AudioContext via the click).
                  playWhisperPing();
                  toast("Whisper chime on");
                } else {
                  toast("Whisper chime muted");
                }
              }}
              className="h-7 w-7 flex items-center justify-center border border-zinc-800 hover:border-red-600 hover:text-red-500 text-zinc-400 transition-colors"
              title={soundMuted ? "Unmute whisper chime" : "Mute whisper chime"}
              aria-label={soundMuted ? "Unmute whisper chime" : "Mute whisper chime"}
              aria-pressed={soundMuted}
              data-testid="chat-whisper-mute-btn"
            >
              {soundMuted ? (
                <BellOff className="w-3.5 h-3.5" />
              ) : (
                <Bell className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                // Toggle inbox: if a thread is open close it, otherwise open the
                // most-recent unread thread (or the first thread we have).
                if (whisperPeer) {
                  closeWhisper();
                  return;
                }
                const unread = whispers
                  .filter((w) => w.to_user_id === session.user_id && !w.read_by_recipient)
                  .sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1))[0];
                if (unread) {
                  openWhisper({ user_id: unread.from_user_id, nickname: unread.from_nickname });
                } else {
                  toast("No whispers yet — click a name in the online list to start one.");
                }
              }}
              className="relative h-7 w-7 flex items-center justify-center border border-zinc-800 hover:border-red-600 hover:text-red-500 text-zinc-400 transition-colors"
              title="Whispers"
              aria-label="Whispers"
              data-testid="chat-whispers-btn"
            >
              <Mail className="w-3.5 h-3.5" />
              {unreadWhisperCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[9px] font-display tracking-tight leading-none flex items-center justify-center"
                  data-testid="chat-whispers-unread"
                >
                  {unreadWhisperCount > 9 ? "9+" : unreadWhisperCount}
                </span>
              )}
            </button>
            </>
          )}
          {/* Online count — click to see the list of listeners */}
          <Popover open={onlineUsersOpen} onOpenChange={(o) => {
            setOnlineUsersOpen(o);
            if (o) fetchOnlineUsers();
          }}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-xs text-zinc-100 tracking-widest uppercase flex items-center gap-1 hover:text-white"
                title="Who's in chat right now"
                aria-label="Who's in chat right now"
                data-testid="chat-online-count-btn"
              >
                <Users className="w-3 h-3 text-zinc-500" />
                <span
                  className="text-red-500 font-display text-base"
                  data-testid="chat-online-count"
                >
                  {String(online).padStart(3, "0")}
                </span>
                <span className="hidden sm:inline">online</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              sideOffset={8}
              className="w-64 p-0 bg-zinc-950 border border-zinc-700 rounded-none shadow-[0_0_0_1px_rgba(239,68,68,0.25)]"
              data-testid="chat-online-users-panel"
            >
              <div className="px-3 py-2 border-b border-zinc-800 text-[10px] tracking-[0.3em] uppercase text-red-500 font-display">
                //_in_the_pit_now · {onlineUsers.length}
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {onlineUsers.length === 0 && (
                  <div className="px-3 py-3 text-[11px] text-zinc-500 tracking-widest uppercase">
                    //_loading_listeners…
                  </div>
                )}
                {onlineUsers.map((u) => {
                  const isVotw =
                    voterOfTheWeek &&
                    String(u.nickname || "").toLowerCase() === voterOfTheWeek;
                  const isSelf = session?.user_id === u.user_id;
                  return (
                    <div
                      key={u.user_id}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-900 text-sm"
                      data-testid="chat-online-user-row"
                    >
                      <span className="flex-1 min-w-0 truncate flex items-center gap-1.5">
                        {isVotw && (
                          <Crown
                            className="w-3 h-3 text-red-500 shrink-0"
                            fill="currentColor"
                            strokeWidth={0}
                            aria-label="Voter of the Week"
                          />
                        )}
                        <span className={isSelf ? "text-zinc-400 italic" : "text-zinc-100"}>
                          @{u.nickname}
                        </span>
                        {isSelf && (
                          <span className="text-[9px] text-zinc-600 tracking-widest uppercase">
                            (you)
                          </span>
                        )}
                      </span>
                      {!isSelf && session?.user_id && (
                        <button
                          type="button"
                          onClick={() => openWhisper({ user_id: u.user_id, nickname: u.nickname })}
                          className="shrink-0 h-6 px-2 border border-zinc-800 hover:border-red-600 hover:text-red-500 text-[10px] tracking-widest uppercase font-display text-zinc-400 transition-colors"
                          title={`Whisper to @${u.nickname}`}
                          aria-label={`Whisper to ${u.nickname}`}
                          data-testid="chat-online-user-whisper"
                        >
                          Whisper
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Today's Vibe — community-driven banner showing the most-quoted
          track in The Pit over the last 24h. Auto-hides when there's no
          clear leader. */}
      <ChatVoteBanner />
      <TodaysVibeBanner onQuoteTrack={quoteTrack} />

      {/* Contest pitch chip — dedicated CTA for mobile-app listeners who
          live inside The Pit and rarely visit the homepage. Opens the
          /contest pitch page in a new tab so listeners don't lose their
          chat scroll position. Hidden if the listener has already backed
          DRR (localStorage flag mirrors the ContestBanner state). */}

      {/* Messages OR whisper thread */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        {whisperPeer ? (
          <WhisperView
            session={session}
            peer={whisperPeer}
            thread={activeWhisperThread}
            voterOfTheWeek={voterOfTheWeek}
            onClose={closeWhisper}
            draft={whisperDraft}
            setDraft={setWhisperDraft}
            onSend={handleSendWhisper}
            sending={sendingWhisper}
            peerTyping={peerTyping}
            onPingTyping={() => pingTyping(whisperPeer.user_id)}
          />
        ) : (
        <>
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="chat-scroll flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-2 text-sm min-w-0"
          data-testid="chat-messages-list"
        >
          {messages.length === 0 && (
            <div className="text-zinc-600 text-xs tracking-widest uppercase">
              //_no_signal — be the first to break the silence
            </div>
          )}
          {/* "Load older messages" pull-up button — only shows if we have
              messages already AND we haven't exhausted history yet. */}
          {messages.length > 0 && !olderExhausted && (
            <button
              type="button"
              onClick={loadOlderMessages}
              disabled={loadingOlder}
              className="w-full text-[10px] tracking-[0.3em] uppercase border border-zinc-800 hover:border-red-600 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-400 py-2 mb-2 font-display transition-colors"
              data-testid="chat-load-older-btn"
            >
              {loadingOlder ? "//_loading…" : "↑ Load older messages"}
            </button>
          )}
          {messages.length > 0 && olderExhausted && (
            <div
              className="w-full text-center text-[10px] tracking-[0.3em] uppercase text-zinc-600 py-2 mb-2 font-display"
              data-testid="chat-older-exhausted"
            >
              //_beginning_of_chat
            </div>
          )}
          {messages.map((m) => (
            <Message
              key={m.id}
              m={m}
              self={session?.user_id && m.user_id === session.user_id}
              onDelete={handleDeleteMessage}
              voterOfTheWeek={voterOfTheWeek}
              onWhisperClick={openWhisper}
              currentUserId={session?.user_id}
            />
          ))}
        </div>

        {/* "New messages" pill — only visible when the user has scrolled up
            and new messages have arrived. Click to jump back to the latest. */}
        {unread > 0 && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 bg-red-600 hover:bg-white hover:text-black text-white text-[10px] tracking-[0.25em] uppercase font-display px-3 py-1.5 border border-red-700 shadow-[0_4px_20px_rgba(239,68,68,0.45)] flex items-center gap-2 transition-colors"
            data-testid="chat-new-messages-pill"
            aria-label={`Jump to ${unread} new message${unread === 1 ? "" : "s"}`}
          >
            <span>↓ {unread} new</span>
          </button>
        )}
        </>
        )}
      </div>

      {/* Composer / Join — hidden while in a whisper thread (WhisperView
          has its own input) */}
      {!whisperPeer && (
      <div className="border-t border-zinc-800 bg-zinc-950">
        {!session ? (
          <form
            onSubmit={handleJoin}
            className="p-4 flex flex-col gap-3"
            data-testid="chat-join-form"
          >
            <div className="text-xs tracking-[0.3em] text-zinc-300 uppercase">
              //_pick_a_handle
            </div>
            <div className="flex gap-0 border border-zinc-700">
              <input
                value={nicknameInput}
                onChange={(e) => setNicknameInput(e.target.value)}
                maxLength={20}
                placeholder="ENTER NICKNAME"
                className="flex-1 bg-zinc-950 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:bg-black tracking-widest font-mono uppercase"
                data-testid="chat-nickname-input"
              />
              <button
                type="submit"
                className="bg-red-600 hover:bg-white hover:text-black text-white font-display tracking-widest uppercase px-5 transition-colors"
                data-testid="chat-join-btn"
              >
                JOIN
              </button>
            </div>
            <p className="text-xs text-zinc-300 leading-relaxed">
              2–20 chars · letters, digits, _ - .  No signup. No password. Just a name to scream behind.
            </p>
          </form>
        ) : (
          <form
            onSubmit={handleSend}
            className="p-3 flex flex-col gap-2"
            data-testid="chat-send-form"
          >
            <div className="flex items-center justify-between text-xs text-zinc-300 tracking-widest uppercase">
              <span>
                <span className="text-red-500">»</span>{" "}
                <span className="text-zinc-300">{session.nickname}</span>
              </span>
            </div>
            {publicTyping.length > 0 && (
              <div
                className="text-[10px] text-zinc-500 tracking-widest uppercase font-mono"
                data-testid="chat-typing-indicator"
              >
                <span className="text-red-500/80">
                  @{publicTyping.slice(0, 3).join(", @")}
                </span>{" "}
                {publicTyping.length === 1 ? "is" : "are"} typing
                <span className="drr-typing-dots ml-0.5">…</span>
              </div>
            )}
            <div className="flex gap-0 border border-zinc-700">
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (e.target.value.trim()) pingTyping(null);
                }}
                maxLength={500}
                placeholder="SAY SOMETHING LOUD..."
                className="flex-1 min-w-0 bg-zinc-950 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:bg-black tracking-wide font-mono"
                data-testid="chat-message-input"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-red-500 px-3 border-l border-zinc-700 transition-colors flex items-center justify-center"
                    aria-label="Open sticker picker"
                    data-testid="chat-sticker-picker-btn"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  sideOffset={8}
                  className="w-80 p-0 bg-zinc-950 border border-zinc-700 rounded-none shadow-[0_0_0_1px_rgba(239,68,68,0.25)]"
                  data-testid="chat-sticker-picker"
                >
                  <StickerPicker
                    onPick={sendSticker}
                    disabled={sending}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-red-500 px-3 border-l border-zinc-700 transition-colors flex items-center justify-center"
                    aria-label="Open emoji picker"
                    data-testid="chat-emoji-picker-btn"
                  >
                    <Smile className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  sideOffset={8}
                  className="w-72 p-0 bg-zinc-950 border border-zinc-700 rounded-none shadow-[0_0_0_1px_rgba(239,68,68,0.25)]"
                  data-testid="chat-emoji-picker"
                >
                  <EmojiPicker onPick={insertEmoji} />
                </PopoverContent>
              </Popover>
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="bg-red-600 hover:bg-white hover:text-black disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 transition-colors flex items-center gap-2 font-display tracking-widest uppercase"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 tracking-widest uppercase">
              //_pic <span className="text-red-500">[img]url[/img]</span> · color <span className="text-red-500">[color=red]hi[/color]</span> · scroll <span className="text-red-500">[scroll]hi[/scroll]</span> · smileys <span className="text-red-500">:) :D ;) \m/ &lt;3</span>
            </p>
          </form>
        )}

        {/* "Now Spinning" + recently-played dropdown — moved down to live
            with the bonus stream so the entire now-playing/voting cluster
            (live title · history ▾ · flame · heart · volume) is in one
            cohesive bar at the bottom of The Pit. Stays visible even
            before joining so visitors can see what's spinning. */}
        <NowPlayingStrip nickname={session?.nickname || ""} onQuoteTrack={quoteTrack} />

        {/* Radiojar mini player — custom brutalist player matching DRR theme.
            Hidden on small screens (mobile apps have their own player) and
            only shows after the listener joins the chat. */}
        {session && (
          <div
            className="hidden md:block"
            data-testid="chat-radiojar-wrapper"
          >
            <RadiojarPlayer nickname={session?.nickname} />
          </div>
        )}
      </div>
      )}
    </section>
  );
}

function trackKey(title, artist) {
  return `${(artist || "").trim().toLowerCase()}|${(title || "").trim().toLowerCase()}`;
}

/**
 * TodaysVibeBanner — single-line community signal at the top of The Pit.
 *
 * Polls /api/todays-vibe every 5 minutes (matches backend cache TTL) and
 * renders a tiny banner showing the track most-shouted-out in chat over
 * the past 24h. Auto-hides when there's no clear leader, so it never
 * clutters the chat. Clicking the banner re-stamps the Vibe-Check
 * citation into the chat input so other listeners can pile on.
 */
function TodaysVibeBanner({ onQuoteTrack }) {
  const [vibe, setVibe] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchVibe = async () => {
      try {
        const res = await axios.get(`${API}/todays-vibe`);
        if (cancelled) return;
        setVibe(res?.data?.ok ? res.data : null);
      } catch (e) {
        if (!cancelled) setVibe(null);
      }
    };
    fetchVibe();
    const id = setInterval(fetchVibe, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!vibe?.ok || !vibe.title) return null;

  const label = vibe.artist ? `"${vibe.title}" by ${vibe.artist}` : `"${vibe.title}"`;
  const shoutoutsLabel = `${vibe.shoutouts} shoutout${vibe.shoutouts === 1 ? "" : "s"}`;

  const handleClick = () => {
    if (onQuoteTrack) onQuoteTrack(vibe.title, vibe.artist || "");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full border-b border-red-600/40 bg-red-600/10 px-4 py-1.5 flex items-center gap-2 text-[11px] text-zinc-300 hover:bg-red-600/20 transition-colors text-left"
      data-testid="todays-vibe-banner"
      data-shoutouts={vibe.shoutouts}
      data-voices={vibe.voices}
      title="Click to drop this track citation into your chat draft"
    >
      <span className="shrink-0 text-red-500 tracking-[0.3em] uppercase font-display">
        //_todays_vibe
      </span>
      <span className="flex-1 min-w-0 truncate text-zinc-100">
        <span className="text-red-400">\m/</span>{" "}
        <span className="font-medium">{label}</span>
      </span>
      <span className="shrink-0 text-zinc-500 tracking-widest uppercase">
        {shoutoutsLabel} · {vibe.voices} voices
      </span>
    </button>
  );
}

function NowPlayingStrip({ nickname, onQuoteTrack }) {
  const np = useNowPlaying();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favKeys, setFavKeys] = useState(() => new Set());

  // Load this listener's favorited track_keys so we can render filled
  // hearts on the rows they've already liked. Refreshed whenever the panel
  // opens or the live track changes (so the live-row heart state stays
  // accurate across track changes).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const sid = getOrCreateSessionId();
    Promise.all([
      axios.get(`${API}/recently-played`, { params: { limit: 10 } }),
      axios.get(`${API}/track-favorites/mine`, { params: { session_id: sid } }),
    ])
      .then(([histRes, favRes]) => {
        if (cancelled) return;
        setHistory(Array.isArray(histRes.data) ? histRes.data : []);
        setFavKeys(new Set(favRes?.data?.track_keys || []));
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, np?.title, np?.artist]);

  const toggleFavorite = useCallback(async (title, artist) => {
    const tk = trackKey(title, artist);
    const sid = getOrCreateSessionId();
    const wasFav = favKeys.has(tk);
    // Optimistic flip
    setFavKeys((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(tk);
      else next.add(tk);
      return next;
    });
    try {
      await axios.post(`${API}/track-favorite`, {
        title,
        artist: artist || "",
        session_id: sid,
        action: wasFav ? "remove" : "add",
        // Send the listener's chat nickname (if they've joined) so the
        // public Top 10 wall can credit them as "Voted by @nick".
        nickname: nickname || "",
      });
    } catch (e) {
      // Roll back the optimistic toggle
      setFavKeys((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(tk);
        else next.delete(tk);
        return next;
      });
      toast.error("Couldn't save favorite");
    }
  }, [favKeys, nickname]);

  if (!np?.ok || !np.title) return null;
  const label = np.artist ? `${np.artist} — ${np.title}` : np.title;
  const liveKey = trackKey(np.title, np.artist);
  const liveFav = favKeys.has(liveKey);

  return (
    <div className="border-b border-zinc-800 bg-zinc-950/90" data-testid="chat-now-playing">
      <div className="w-full px-4 py-2 flex items-center gap-2 text-xs min-w-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 min-w-0 flex items-center gap-2 hover:opacity-80 transition-opacity text-left"
          aria-expanded={open}
          data-testid="chat-now-playing-toggle"
        >
          <span className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-red-500 font-display flex items-center gap-1.5">
            <span className="on-air-dot scale-75" aria-hidden />
            Now Spinning
          </span>
          <span
            className="flex-1 min-w-0 truncate text-zinc-200 font-mono tracking-tight"
            title={label}
            data-testid="chat-now-playing-track"
          >
            {label}
          </span>
          <span
            className={`shrink-0 text-zinc-500 text-[10px] tracking-widest uppercase font-display transition-transform ${
              open ? "rotate-180 text-red-500" : ""
            }`}
            aria-hidden
          >
            ▾
          </span>
        </button>
      </div>
      {open && (
        <div
          className="px-4 pb-3 pt-1 border-t border-zinc-800 bg-black/40"
          data-testid="chat-recently-played-panel"
        >
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-display mb-2">
            //_recently_played
          </div>
          {loading && (
            <div className="text-[11px] text-zinc-500 tracking-widest uppercase">
              //_loading…
            </div>
          )}
          {!loading && history.length === 0 && (
            <div
              className="text-[11px] text-zinc-500 tracking-widest uppercase"
              data-testid="chat-recently-played-empty"
            >
              //_no_history_yet · check back in a bit
            </div>
          )}
          {!loading && history.length > 0 && (
            <ol className="space-y-1.5 text-xs">
              {history.map((t, idx) => {
                const query = encodeURIComponent(
                  t.artist ? `${t.artist} ${t.title}` : t.title
                );
                const spotifyUrl = `https://open.spotify.com/search/${query}`;
                const youtubeUrl = `https://www.youtube.com/results?search_query=${query}`;
                const tk = trackKey(t.title, t.artist);
                const isFav = favKeys.has(tk);
                return (
                  <li
                    key={`${t.played_at}-${idx}`}
                    className="group flex items-center gap-2 min-w-0 py-1 px-1 -mx-1 hover:bg-zinc-900/60 transition-colors"
                    data-testid="chat-recently-played-item"
                  >
                    <span className="shrink-0 text-[10px] text-zinc-600 font-display tracking-widest w-6">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div
                        className="truncate text-zinc-100 font-mono"
                        title={t.title}
                      >
                        {t.title}
                      </div>
                      {t.artist && (
                        <div
                          className="truncate text-[10px] text-zinc-500 tracking-wide"
                          title={t.artist}
                        >
                          {t.artist}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => toggleFavorite(t.title, t.artist)}
                        title={isFav ? "Unfavorite" : "Favorite this track"}
                        aria-label={isFav ? "Unfavorite" : "Favorite this track"}
                        className={`w-6 h-6 flex items-center justify-center border transition-colors ${
                          isFav
                            ? "text-red-500 border-red-600 bg-red-600/10 !opacity-100"
                            : "text-zinc-400 border-zinc-800 hover:text-red-500 hover:border-red-600 hover:bg-zinc-900"
                        }`}
                        data-testid="chat-recently-played-heart"
                      >
                        <Heart
                          className="w-3.5 h-3.5"
                          strokeWidth={2.2}
                          fill={isFav ? "currentColor" : "none"}
                        />
                      </button>
                      {onQuoteTrack && (
                        <button
                          type="button"
                          onClick={() => onQuoteTrack(t.title, t.artist)}
                          title="Shout out this track in chat"
                          aria-label={`Shout out ${t.title} in chat`}
                          className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-zinc-900 border border-zinc-800 hover:border-red-600 transition-colors"
                          data-testid="chat-recently-played-vibe"
                        >
                          <MessageSquareQuote
                            className="w-3.5 h-3.5"
                            strokeWidth={2.2}
                          />
                        </button>
                      )}
                      <a
                        href={spotifyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Find "${t.title}" on Spotify`}
                        aria-label={`Find ${t.title} on Spotify`}
                        className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-[#1DB954] hover:bg-zinc-900 border border-zinc-800 hover:border-[#1DB954] transition-colors"
                        data-testid="chat-recently-played-spotify"
                      >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                      </a>
                      <a
                        href={youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Find "${t.title}" on YouTube`}
                        aria-label={`Find ${t.title} on YouTube`}
                        className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-[#FF0000] hover:bg-zinc-900 border border-zinc-800 hover:border-[#FF0000] transition-colors"
                        data-testid="chat-recently-played-youtube"
                      >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </a>
                    </div>
                    <span className="shrink-0 text-[10px] text-zinc-600 tracking-widest w-10 text-right">
                      {formatTimeAgo(t.played_at)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function formatTimeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return `${diffSec}s`;
    const m = Math.floor(diffSec / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  } catch (e) {
    return "";
  }
}

function WhisperView({
  session,
  peer,
  thread,
  voterOfTheWeek,
  onClose,
  draft,
  setDraft,
  onSend,
  sending,
  peerTyping,
  onPingTyping,
}) {
  const scrollRef = useRef(null);
  // Auto-scroll to the latest whisper whenever the thread length changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length]);

  const peerIsVotw =
    voterOfTheWeek && String(peer.nickname || "").toLowerCase() === voterOfTheWeek;

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="chat-whisper-view">
      <div className="border-b border-red-900 bg-red-950/30 px-3 py-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-7 w-7 flex items-center justify-center border border-zinc-800 hover:border-red-600 hover:text-red-500 text-zinc-300 transition-colors"
          title="Back to public chat"
          aria-label="Back to public chat"
          data-testid="chat-whisper-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] tracking-[0.3em] uppercase text-red-500 font-display">
            //_whisper_with
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            {peerIsVotw && (
              <Crown
                className="w-3 h-3 text-red-500 shrink-0"
                fill="currentColor"
                strokeWidth={0}
                aria-label="Voter of the Week"
              />
            )}
            <span
              className="truncate text-zinc-100 font-display tracking-tight"
              data-testid="chat-whisper-peer-nickname"
            >
              @{peer.nickname}
            </span>
          </div>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="chat-scroll flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-2 text-sm min-w-0"
        data-testid="chat-whisper-thread"
      >
        {thread.length === 0 && (
          <div className="text-zinc-600 text-xs tracking-widest uppercase">
            //_no_history — drop them a line to start
          </div>
        )}
        {thread.map((w) => {
          const fromSelf = w.from_user_id === session?.user_id;
          const time = (() => {
            try {
              const d = new Date(w.timestamp);
              return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
            } catch (e) {
              return "";
            }
          })();
          return (
            <div
              key={w.id}
              className={`group pl-2 border-l-2 ${
                fromSelf ? "border-orange-500" : "border-red-600"
              } leading-snug min-w-0`}
              data-testid="chat-whisper-row"
            >
              <div className="flex items-baseline gap-2 min-w-0">
                <span
                  className={`font-display text-sm ${
                    fromSelf ? "text-orange-400" : "text-red-500"
                  } tracking-tight truncate max-w-[60%]`}
                >
                  {fromSelf ? "YOU" : w.from_nickname}
                </span>
                <span className="text-[10px] text-zinc-600 tracking-widest shrink-0">
                  [{time}]
                </span>
              </div>
              <div
                className="text-zinc-200 whitespace-pre-wrap min-w-0 mt-0.5"
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              >
                {w.text}
              </div>
            </div>
          );
        })}
      </div>
      <form
        onSubmit={onSend}
        className="border-t border-zinc-800 bg-zinc-950 p-3 flex flex-col gap-2"
        data-testid="chat-whisper-send-form"
      >
        <div className="flex items-center justify-between gap-2 min-h-[14px]">
          <div className="text-[10px] text-zinc-500 tracking-widest uppercase">
            //_only @{peer.nickname} can read this
          </div>
          {peerTyping && (
            <div
              className="text-[10px] text-red-500/80 tracking-widest uppercase font-mono"
              data-testid="chat-whisper-typing-indicator"
            >
              @{peer.nickname} is typing
              <span className="drr-typing-dots ml-0.5">…</span>
            </div>
          )}
        </div>
        <div className="flex gap-0 border border-red-900">
          <input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (e.target.value.trim() && typeof onPingTyping === "function") {
                onPingTyping();
              }
            }}
            maxLength={2000}
            placeholder={`WHISPER TO @${peer.nickname.toUpperCase()}…`}
            className="flex-1 min-w-0 bg-zinc-950 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:bg-black tracking-wide font-mono"
            data-testid="chat-whisper-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            className="bg-red-600 hover:bg-white hover:text-black disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 transition-colors flex items-center justify-center"
            data-testid="chat-whisper-send-btn"
            aria-label="Send whisper"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}


function Message({ m, self, onDelete, voterOfTheWeek, onWhisperClick, currentUserId }) {
  const isSystem = m.nickname === "//SYSTEM";
  const nickLower = String(m.nickname || "").toLowerCase();
  const isVotw = !isSystem && voterOfTheWeek && nickLower === voterOfTheWeek;
  const canWhisper =
    !isSystem &&
    !self &&
    !!currentUserId &&
    !!m.user_id &&
    typeof onWhisperClick === "function";
  const time = (() => {
    try {
      const d = new Date(m.timestamp);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "";
    }
  })();

  if (isSystem) {
    return (
      <div
        className="text-xs text-zinc-600 italic tracking-widest uppercase"
        data-testid="chat-system-message"
      >
        // {m.message}
      </div>
    );
  }

  // Show the delete X only on the user's own messages, and only inside the
  // 5-min window the backend will accept. After that it just looks broken,
  // so we hide it client-side to match.
  const ageMs = (() => {
    try {
      return Date.now() - new Date(m.timestamp).getTime();
    } catch (e) {
      return Infinity;
    }
  })();
  const canDelete = self && onDelete && ageMs < 5 * 60 * 1000;

  return (
    <div
      className={`group relative pl-2 border-l-2 ${
        self ? "border-orange-500" : "border-red-600"
      } leading-snug min-w-0`}
      data-testid="chat-message-row"
    >
      <div className="flex items-baseline gap-2 min-w-0">
        {canWhisper ? (
          <button
            type="button"
            onClick={() => onWhisperClick({ user_id: m.user_id, nickname: m.nickname })}
            className={`font-display text-base ${
              self ? "text-orange-400" : "text-red-500"
            } tracking-tight truncate max-w-[60%] hover:underline underline-offset-2 hover:text-white text-left flex items-center gap-1 min-w-0`}
            title={`Whisper to @${m.nickname}`}
            data-testid="chat-message-nickname-btn"
          >
            {isVotw && (
              <Crown
                className="w-3 h-3 text-red-500 shrink-0"
                fill="currentColor"
                strokeWidth={0}
                aria-label="Voter of the Week"
              />
            )}
            <span className="truncate">{m.nickname}</span>
          </button>
        ) : (
          <span
            className={`font-display text-base ${
              self ? "text-orange-400" : "text-red-500"
            } tracking-tight truncate max-w-[60%] flex items-center gap-1 min-w-0`}
          >
            {isVotw && (
              <Crown
                className="w-3 h-3 text-red-500 shrink-0"
                fill="currentColor"
                strokeWidth={0}
                aria-label="Voter of the Week"
              />
            )}
            <span className="truncate">{m.nickname}</span>
          </span>
        )}
        <span className="text-[10px] text-zinc-600 tracking-widest shrink-0">
          [{time}]
        </span>
        {canDelete && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm("Delete this message?")) onDelete(m.id);
            }}
            className="ml-auto shrink-0 p-1 text-zinc-500 hover:text-red-500 hover:bg-zinc-900 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label="Delete this message"
            title="Delete (within 5 min)"
            data-testid={`chat-delete-${m.id}`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
        )}
      </div>
      <div
        className="text-zinc-200 whitespace-pre-wrap min-w-0"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      >
        {renderMessageBody(m.message)}
      </div>
      <LinkPreview url={extractFirstPreviewableUrl(m.message)} />
    </div>
  );
}

/**
 * Parses [img]URL[/img] BBCode-style tags in chat messages and renders
 * them as inline images. Only http(s) URLs are allowed (no javascript:,
 * data:, etc.) for safety. Plain text outside the tags is preserved.
 */
/**
 * Parses BBCode-style tags in chat messages:
 *   [img]URL[/img]                          → inline image
 *   [color=red]text[/color]                 → colored text (whitelisted names + hex)
 *   [scroll]text[/scroll]                   → slow marquee scroll (~15s, capped 80 chars)
 *
 * Tags can be combined: [color=red][scroll]hello[/scroll][/color]
 * Unknown / unclosed tags render as plain text.
 */
const COLOR_WHITELIST = {
  red: "#ef4444",
  orange: "#f97316",
  yellow: "#facc15",
  green: "#22c55e",
  blue: "#3b82f6",
  cyan: "#06b6d4",
  purple: "#a855f7",
  pink: "#ec4899",
  white: "#ffffff",
  gray: "#a1a1aa",
};
const SCROLL_MAX_LEN = 80;
const ANY_TAG = /\[(\/?(?:img|color(?:=[^\]]+)?|scroll))\]/gi;

function resolveColor(rawArg) {
  if (!rawArg) return null;
  const v = rawArg.trim().toLowerCase();
  if (COLOR_WHITELIST[v]) return COLOR_WHITELIST[v];
  // allow short or long hex codes
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  return null;
}

function renderMessageBody(text) {
  if (!text) return null;
  const nodes = parseBBCode(text);
  return renderNodes(nodes);
}

/**
 * Parse the message into a tree of plain-text + tag nodes.
 * Node shapes:
 *   { type: "text", value: "..." }
 *   { type: "img", url: "..." }
 *   { type: "color", color: "#...", children: [...] }
 *   { type: "scroll", children: [...] }
 */
function parseBBCode(text) {
  ANY_TAG.lastIndex = 0;
  const tokens = [];
  let last = 0;
  let m;
  while ((m = ANY_TAG.exec(text)) !== null) {
    if (m.index > last) tokens.push({ kind: "text", value: text.slice(last, m.index) });
    tokens.push({ kind: "tag", raw: m[1] });
    last = ANY_TAG.lastIndex;
  }
  if (last < text.length) tokens.push({ kind: "text", value: text.slice(last) });

  // Walk tokens, build nested structure
  const root = { type: "root", children: [] };
  const stack = [root];
  let imgOpen = false; // [img]...[/img] swallows inner text as URL

  for (const tok of tokens) {
    const top = stack[stack.length - 1];
    if (tok.kind === "text") {
      if (imgOpen) {
        const url = tok.value.trim();
        if (/^https?:\/\/\S+$/i.test(url)) top.children.push({ type: "img", url });
        else top.children.push({ type: "text", value: `[img]${tok.value}` });
      } else {
        top.children.push({ type: "text", value: tok.value });
      }
      continue;
    }

    const tag = tok.raw.toLowerCase();

    if (tag === "img") { imgOpen = true; continue; }
    if (tag === "/img") { imgOpen = false; continue; }

    if (tag === "scroll") {
      const node = { type: "scroll", children: [] };
      top.children.push(node);
      stack.push(node);
      continue;
    }
    if (tag === "/scroll") {
      if (top.type === "scroll") stack.pop();
      else top.children.push({ type: "text", value: "[/scroll]" });
      continue;
    }

    if (tag.startsWith("color=")) {
      const arg = tok.raw.slice("color=".length);
      const color = resolveColor(arg);
      if (color) {
        const node = { type: "color", color, children: [] };
        top.children.push(node);
        stack.push(node);
      } else {
        top.children.push({ type: "text", value: `[${tok.raw}]` });
      }
      continue;
    }
    if (tag === "/color") {
      if (top.type === "color") stack.pop();
      else top.children.push({ type: "text", value: "[/color]" });
      continue;
    }

    // Unknown — keep literal
    top.children.push({ type: "text", value: `[${tok.raw}]` });
  }

  // Close any unclosed scroll/color implicitly (no extra text emitted)
  return root.children;
}

function renderNodes(nodes, keyPrefix = "n") {
  return nodes.map((n, i) => {
    const k = `${keyPrefix}-${i}`;
    if (n.type === "text") return renderTextWithLinksAndEmoticons(n.value, k);
    if (n.type === "img") {
      return (
        <a
          key={k}
          href={n.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block my-2"
          data-testid="chat-img-link"
        >
          <img
            src={n.url}
            alt="shared by listener"
            loading="lazy"
            className="max-w-full max-h-64 border border-zinc-700 object-contain bg-black"
            data-testid="chat-img"
            onError={(e) => {
              e.currentTarget.replaceWith(
                Object.assign(document.createElement("span"), {
                  className: "text-xs text-red-500 tracking-widest uppercase",
                  textContent: "// image_failed_to_load",
                })
              );
            }}
          />
        </a>
      );
    }
    if (n.type === "color") {
      return (
        <span key={k} style={{ color: n.color }} data-testid="chat-color-span">
          {renderNodes(n.children, k)}
        </span>
      );
    }
    if (n.type === "scroll") {
      // Cap total inner text length; render via inline marquee block.
      const inner = collectText(n.children).slice(0, SCROLL_MAX_LEN);
      // Use a children pass so colors inside [scroll] still render — but
      // limit visible content if too long.
      const limitedChildren =
        collectText(n.children).length > SCROLL_MAX_LEN
          ? [{ type: "text", value: inner + "…" }]
          : n.children;
      return (
        <span
          key={k}
          className="drr-scroll-marquee"
          data-testid="chat-scroll-marquee"
        >
          <span className="drr-scroll-marquee-track">
            {renderNodes(limitedChildren, k)}
          </span>
        </span>
      );
    }
    return null;
  });
}

function collectText(nodes) {
  let s = "";
  for (const n of nodes) {
    if (n.type === "text") s += n.value;
    else if (n.children) s += collectText(n.children);
  }
  return s;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Emoticons
 *
 * Auto-convert classic text smileys into emoji and provide a click-to-insert
 * picker. Metal-themed emoji are featured up top, classic emoji below.
 * Smileys are only converted when surrounded by whitespace or message edges
 * so URLs / BBCode arguments don't get mangled (e.g. "http://" stays intact).
 * ──────────────────────────────────────────────────────────────────────── */

// NOTE: order matters — longest patterns must come first so ":-)" wins over ":)".
const EMOTICONS = [
  [":'(", "😢"],
  [":-)", "🙂"],
  [":-D", "😄"],
  [":-(", "😞"],
  [";-)", "😉"],
  [":-P", "😛"],
  [":-p", "😛"],
  [":-O", "😮"],
  [":-o", "😮"],
  [":-|", "😐"],
  [":-/", "😕"],
  [":-*", "😘"],
  ["</3", "💔"],
  ["\\m/", "🤘"],
  ["XD", "😆"],
  ["xD", "😆"],
  ["B)", "😎"],
  ["<3", "❤️"],
  [":)", "🙂"],
  [":D", "😄"],
  [":(", "😞"],
  [";)", "😉"],
  [":P", "😛"],
  [":p", "😛"],
  [":O", "😮"],
  [":o", "😮"],
  [":|", "😐"],
  [":/", "😕"],
  [":*", "😘"],
];

const EMOTICON_MAP = Object.fromEntries(EMOTICONS);
const EMOTICON_RE = (() => {
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$1");
  const alt = EMOTICONS.map(([k]) => esc(k)).join("|");
  // Match smiley only when preceded by start/whitespace and followed by end/whitespace/punctuation.
  return new RegExp(`(^|\\s)(${alt})(?=\\s|$|[.,!?;:])`, "g");
})();

function renderTextWithEmoticons(text, keyPrefix) {
  if (!text) return null;
  const out = [];
  let last = 0;
  let i = 0;
  EMOTICON_RE.lastIndex = 0;
  let m;
  while ((m = EMOTICON_RE.exec(text)) !== null) {
    const matchStart = m.index + m[1].length; // skip leading whitespace
    if (matchStart > last) {
      out.push(
        <span key={`${keyPrefix}-t${i++}`}>{text.slice(last, matchStart)}</span>
      );
    }
    const emoji = EMOTICON_MAP[m[2]] || m[2];
    out.push(
      <span
        key={`${keyPrefix}-e${i++}`}
        className="drr-emoticon"
        data-testid="chat-emoticon"
        aria-label={m[2]}
      >
        {emoji}
      </span>
    );
    last = matchStart + m[2].length;
  }
  if (last < text.length) {
    out.push(<span key={`${keyPrefix}-t${i++}`}>{text.slice(last)}</span>);
  }
  return <span key={keyPrefix}>{out}</span>;
}

/* @mention detector — captures any @word (letters / digits / underscore)
 * preceded by a non-word character or string start. The capture group is
 * the nickname WITHOUT the leading "@". */
const MENTION_RE = /(^|[^A-Za-z0-9_])@([A-Za-z0-9_]{2,32})/g;

function renderTextWithEmoticonsAndMentions(text, keyPrefix) {
  if (!text) return null;
  const out = [];
  let last = 0;
  let i = 0;
  MENTION_RE.lastIndex = 0;
  let m;
  while ((m = MENTION_RE.exec(text)) !== null) {
    const lead = m[1] || "";
    const nick = m[2];
    const mentionStart = m.index + lead.length;
    // Render any text before the mention through the emoticon pass.
    if (mentionStart > last) {
      const chunk = text.slice(last, mentionStart);
      const node = renderTextWithEmoticons(chunk, `${keyPrefix}-pre${i++}`);
      if (node) out.push(node);
    }
    out.push(
      <span
        key={`${keyPrefix}-m${i++}`}
        className="text-red-500 font-medium"
        data-testid="chat-mention"
        data-nickname={nick}
      >
        @{nick}
      </span>
    );
    last = mentionStart + 1 + nick.length; // "@" + nick
  }
  if (last < text.length) {
    const tail = renderTextWithEmoticons(text.slice(last), `${keyPrefix}-tail${i++}`);
    if (tail) out.push(tail);
  }
  return <span key={keyPrefix}>{out}</span>;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Auto-linker
 *
 * Detects plain http(s) URLs and bare "www." URLs typed into chat and turns
 * them into clickable links. Runs BEFORE emoticon parsing so a "B)" inside a
 * URL doesn't get replaced with 😎, and so emoticon parsing only sees the
 * non-URL parts of the text. Trailing sentence punctuation like ).,!?;:'"`
 * is stripped off the match so it isn't swallowed into the link.
 * ──────────────────────────────────────────────────────────────────────── */
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;
const URL_TRAIL = /[)\].,!?;:'"`]+$/;

function renderTextWithLinksAndEmoticons(text, keyPrefix) {
  if (!text) return null;
  const out = [];
  let last = 0;
  let i = 0;
  URL_RE.lastIndex = 0;
  let m;
  while ((m = URL_RE.exec(text)) !== null) {
    let raw = m[0];
    const trail = raw.match(URL_TRAIL);
    if (trail) {
      raw = raw.slice(0, raw.length - trail[0].length);
      URL_RE.lastIndex -= trail[0].length;
    }
    if (!raw) continue;
    const start = m.index;
    if (start > last) {
      const pre = renderTextWithEmoticonsAndMentions(text.slice(last, start), `${keyPrefix}-pre${i++}`);
      if (pre) out.push(pre);
    }
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    out.push(
      <a
        key={`${keyPrefix}-u${i++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow ugc"
        className="text-red-400 underline underline-offset-2 hover:text-white break-all"
        data-testid="chat-link"
      >
        {raw}
      </a>
    );
    last = start + raw.length;
  }
  if (last < text.length) {
    const tail = renderTextWithEmoticonsAndMentions(text.slice(last), `${keyPrefix}-tail${i++}`);
    if (tail) out.push(tail);
  }
  return <span key={keyPrefix}>{out}</span>;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Link previews
 *
 * For the first URL in a message, fetch Open Graph metadata from
 * /api/link-preview and render a small "card" beneath the message body
 * (favicon · site · title · description · thumbnail). Falls back silently
 * if the preview API returns nothing.
 *
 * Module-scoped cache so the same URL across many messages only hits the
 * API once per browser tab — avoids hammering the endpoint when chat
 * history is scrolled.
 * ──────────────────────────────────────────────────────────────────────── */
const PREVIEW_CACHE = new Map(); // url -> { ok: bool, preview: {...}|null }
const PREVIEW_INFLIGHT = new Map(); // url -> Promise

// Find the first link in a message that should get a preview card. We strip
// out [img]...[/img] blocks first so an inline image URL doesn't also get a
// duplicate card. Returns a fully-qualified https URL or null.
function extractFirstPreviewableUrl(text) {
  if (!text) return null;
  const stripped = text.replace(/\[img\][\s\S]*?\[\/img\]/gi, " ");
  URL_RE.lastIndex = 0;
  const match = URL_RE.exec(stripped);
  if (!match) return null;
  let raw = match[0];
  const trail = raw.match(URL_TRAIL);
  if (trail) raw = raw.slice(0, raw.length - trail[0].length);
  if (!raw) return null;
  return raw.startsWith("http") ? raw : `https://${raw}`;
}

function LinkPreview({ url }) {
  const [data, setData] = useState(() =>
    url && PREVIEW_CACHE.has(url) ? PREVIEW_CACHE.get(url) : null
  );

  useEffect(() => {
    if (!url) return;
    if (PREVIEW_CACHE.has(url)) {
      setData(PREVIEW_CACHE.get(url));
      return;
    }
    let cancelled = false;
    const inflight =
      PREVIEW_INFLIGHT.get(url) ||
      axios
        .get(`${API}/link-preview`, { params: { url } })
        .then((res) => {
          const payload = res?.data || { ok: false };
          PREVIEW_CACHE.set(url, payload);
          return payload;
        })
        .catch(() => {
          const payload = { ok: false };
          PREVIEW_CACHE.set(url, payload);
          return payload;
        })
        .finally(() => {
          PREVIEW_INFLIGHT.delete(url);
        });
    PREVIEW_INFLIGHT.set(url, inflight);
    inflight.then((payload) => {
      if (!cancelled) setData(payload);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!url || !data || !data.ok || !data.preview) return null;
  const p = data.preview;
  const title = p.title || p.site_name || p.url;
  if (!title && !p.image && !p.description) return null;

  return (
    <a
      href={p.url || url}
      target="_blank"
      rel="noopener noreferrer nofollow ugc"
      className="mt-2 flex gap-3 border border-zinc-800 hover:border-red-600 bg-zinc-900/60 hover:bg-zinc-900 p-2 max-w-md transition-colors group"
      data-testid="chat-link-preview"
    >
      {p.image && (
        <img
          src={p.image}
          alt=""
          loading="lazy"
          className="w-20 h-20 object-cover bg-black border border-zinc-800 shrink-0"
          data-testid="chat-link-preview-image"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 tracking-widest uppercase">
          {p.favicon && (
            <img
              src={p.favicon}
              alt=""
              loading="lazy"
              className="w-3 h-3"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <span className="truncate" data-testid="chat-link-preview-site">
            {p.site_name || new URL(p.url || url).hostname}
          </span>
        </div>
        <div
          className="text-sm text-zinc-100 group-hover:text-red-400 font-display tracking-tight line-clamp-2 mt-0.5"
          data-testid="chat-link-preview-title"
        >
          {title}
        </div>
        {p.description && (
          <div
            className="text-xs text-zinc-400 line-clamp-2 mt-0.5"
            data-testid="chat-link-preview-description"
          >
            {p.description}
          </div>
        )}
      </div>
    </a>
  );
}



/* Curated emoji set for the picker. Metal first, then classics. */
const METAL_EMOJI = ["🤘", "🔥", "💀", "⚡", "🎸", "🥁", "🎤", "🍺", "👹", "⚰️", "🤟", "🖤"];
const CLASSIC_EMOJI = [
  "🙂", "😄", "😉", "😎", "😂", "🤣", "😍", "😘", "😛", "😮",
  "😢", "😡", "😱", "🤔", "🤯", "🙄", "😬", "😴", "🤩", "🥳",
  "👍", "👎", "👌", "👏", "🙏", "💪", "✊", "🫡", "👀", "💯",
  "❤️", "💔", "🎉", "🚀", "⭐", "✨",
];

/**
 * Admin-curated sticker picker shown next to the emoji button. Fetches the
 * sticker list from /api/chat-stickers on open (cheap — public endpoint).
 * Click → auto-sends as a [img]…[/img] message via the parent's onPick
 * callback. Gracefully falls back when the list is empty so the picker
 * never looks broken if the admin hasn't added any yet.
 */
function StickerPicker({ onPick, disabled }) {
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-browser sticker recents (most-recent first, capped at 6). Stored as
  // an ordered array of IDs so we still respect deletions / renames upstream.
  const RECENTS_KEY = "drr_recent_stickers";
  const RECENTS_CAP = 6;
  const [recentIds, setRecentIds] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
      return Array.isArray(raw) ? raw.filter((x) => typeof x === "string").slice(0, RECENTS_CAP) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/chat-stickers`)
      .then((res) => {
        if (cancelled) return;
        setStickers(res?.data?.stickers || []);
      })
      .catch(() => {
        if (!cancelled) setStickers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Wrap the parent's onPick so we can record the pick in local recents.
  // The actual send still flows through the parent (sendSticker) which
  // handles the chat POST + counter bump.
  const handlePick = (sticker) => {
    if (!sticker?.id) return;
    onPick?.(sticker);
    setRecentIds((prev) => {
      const next = [sticker.id, ...prev.filter((id) => id !== sticker.id)].slice(0, RECENTS_CAP);
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch (e) {
        /* private mode / quota — local-only feature is best-effort */
      }
      return next;
    });
  };

  // Materialize recent IDs → sticker objects, dropping any that have been
  // deleted upstream. Preserves the most-recent-first order from localStorage.
  const byId = new Map(stickers.map((s) => [s.id, s]));
  const recent = recentIds.map((id) => byId.get(id)).filter(Boolean);
  const recentSet = new Set(recent.map((s) => s.id));

  // HOT excludes anything already in RECENT (no double-render) and is built
  // from the remaining stickers sorted by use_count desc, > 0 only.
  const hot = [...stickers]
    .filter((s) => !recentSet.has(s.id) && (s.use_count || 0) > 0)
    .sort((a, b) => (b.use_count || 0) - (a.use_count || 0))
    .slice(0, 6);
  const hotSet = new Set(hot.map((s) => s.id));

  // ALL = everything not in RECENT or HOT, preserving backend newest-first.
  const rest = stickers.filter((s) => !recentSet.has(s.id) && !hotSet.has(s.id));

  const renderTile = (s) => (
    <li key={s.id} className="relative">
      <button
        type="button"
        onClick={() => handlePick(s)}
        disabled={disabled}
        title={s.name + ((s.use_count || 0) > 0 ? ` · used ${s.use_count}× in chat` : "")}
        aria-label={`Send ${s.name}`}
        className="block w-full border border-zinc-800 hover:border-red-600 bg-black overflow-hidden transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="chat-sticker-picker-item"
        data-use-count={s.use_count || 0}
      >
        <img
          src={s.image_url}
          alt={s.name}
          loading="lazy"
          className="w-full aspect-square object-cover"
          onError={(e) => {
            e.currentTarget.style.opacity = "0.3";
          }}
        />
      </button>
    </li>
  );

  return (
    <div className="p-3" data-testid="chat-sticker-picker-content">
      <div className="text-[10px] tracking-[0.3em] text-red-500 uppercase mb-2 flex items-center justify-between">
        <span>//_stickers</span>
        <span className="text-zinc-600">{stickers.length}</span>
      </div>
      {loading && (
        <div className="text-xs text-zinc-500 tracking-widest uppercase">
          //_loading…
        </div>
      )}
      {!loading && stickers.length === 0 && (
        <div className="text-xs text-zinc-500 tracking-widest uppercase leading-relaxed">
          //_no_stickers_yet
          <div className="text-zinc-600 mt-1 normal-case tracking-wide">
            The station hasn&apos;t added any yet — check back soon.
          </div>
        </div>
      )}
      {!loading && stickers.length > 0 && (
        <div className="max-h-80 overflow-y-auto space-y-3">
          {recent.length > 0 && (
            <div data-testid="chat-sticker-picker-recent">
              <div className="text-[9px] tracking-[0.35em] text-zinc-300 uppercase mb-1.5 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 bg-zinc-300" />
                //_recent · your last {recent.length}
              </div>
              <ul className="grid grid-cols-3 gap-2">{recent.map(renderTile)}</ul>
            </div>
          )}
          {hot.length > 0 && (
            <div data-testid="chat-sticker-picker-hot">
              <div className="text-[9px] tracking-[0.35em] text-red-500 uppercase mb-1.5 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 bg-red-500 drr-flame-pulse" />
                //_hot · top {hot.length}
              </div>
              <ul className="grid grid-cols-3 gap-2">{hot.map(renderTile)}</ul>
            </div>
          )}
          {rest.length > 0 && (
            <div>
              {(recent.length > 0 || hot.length > 0) && (
                <div className="text-[9px] tracking-[0.35em] text-zinc-500 uppercase mb-1.5">
                  //_all · {rest.length}
                </div>
              )}
              <ul className="grid grid-cols-3 gap-2">{rest.map(renderTile)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function EmojiPicker({ onPick }) {
  return (
    <div className="font-mono">
      <div className="px-3 py-2 border-b border-zinc-800 text-[10px] tracking-[0.25em] text-red-500 uppercase">
        //_metal
      </div>
      <div className="grid grid-cols-8 gap-1 p-2" data-testid="emoji-picker-metal-row">
        {METAL_EMOJI.map((e) => (
          <button
            key={`m-${e}`}
            type="button"
            onClick={() => onPick(e)}
            className="text-xl leading-none p-1 hover:bg-red-600/20 hover:scale-110 transition-transform rounded-sm"
            aria-label={`Insert ${e}`}
            data-testid={`emoji-pick-${e}`}
          >
            {e}
          </button>
        ))}
      </div>
      <div className="px-3 py-2 border-y border-zinc-800 text-[10px] tracking-[0.25em] text-zinc-400 uppercase">
        //_classic
      </div>
      <div
        className="grid grid-cols-8 gap-1 p-2 max-h-44 overflow-y-auto chat-scroll"
        data-testid="emoji-picker-classic-row"
      >
        {CLASSIC_EMOJI.map((e) => (
          <button
            key={`c-${e}`}
            type="button"
            onClick={() => onPick(e)}
            className="text-xl leading-none p-1 hover:bg-red-600/20 hover:scale-110 transition-transform rounded-sm"
            aria-label={`Insert ${e}`}
            data-testid={`emoji-pick-${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * ContestChip — slim brutalist strip inside The Pit promoting the Emergent ×
 * Fabrizio Romano Builders Contest. Designed specifically for mobile-app
 * listeners who live inside /chat and rarely see the homepage or the top
 * ContestBanner. Two states:
 *   - Not yet backed: "🏆 BACK DRR IN THE CONTEST →" button + I'M ROOTING
 *   - Backed (mine=true): compact acknowledgement chip
 * Dismissible independently from the site-wide ContestBanner (own storage key).
 */
function ContestChip() {
  const CHIP_STORAGE_KEY = "drr_chat_contest_chip_dismissed_v1";
  const [hidden, setHidden] = useState(true);
  const [supporters, setSupporters] = useState(null);
  const [mine, setMine] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setHidden(localStorage.getItem(CHIP_STORAGE_KEY) === "1");
  }, []);

  useEffect(() => {
    if (hidden) return;
    let cancelled = false;
    const load = async () => {
      try {
        const sid = getOrCreateSessionId();
        const res = await axios.get(`${API}/contest/support`, {
          params: { session_id: sid },
        });
        if (cancelled) return;
        setSupporters(res?.data?.total ?? 0);
        setMine(!!res?.data?.mine);
      } catch (e) {
        /* silent */
      }
    };
    load();
    // Refresh every 60s so mobile listeners see the count tick up as fans
    // click through from social shares.
    const t = setInterval(load, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [hidden]);

  const handleRoot = async () => {
    if (mine || submitting) return;
    setSubmitting(true);
    setMine(true);
    setSupporters((s) => (typeof s === "number" ? s + 1 : s));
    try {
      const sid = getOrCreateSessionId();
      const res = await axios.post(`${API}/contest/support`, {
        session_id: sid,
        nickname: (localStorage.getItem("drr_chat_nickname") || "").trim(),
      });
      setSupporters(res?.data?.total ?? supporters);
      toast.success("🤘 You're on the wall — thanks for backing DRR");
    } catch (e) {
      setMine(false);
      setSupporters((s) => (typeof s === "number" ? Math.max(0, s - 1) : s));
      toast.error("Couldn't record your support — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (hidden) return null;

  const supportersLabel =
    typeof supporters === "number"
      ? `${supporters.toLocaleString()} ${supporters === 1 ? "SUPPORTER" : "SUPPORTERS"}`
      : "— SUPPORTERS";

  return (
    <div
      className="relative border-b border-zinc-800 bg-red-950/40 px-3 py-1.5 flex items-center gap-2 flex-wrap"
      data-testid="chat-contest-chip"
    >
      <span className="text-red-400 shrink-0">
        <Trophy className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
      <a
        href="/contest"
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 min-w-0 text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-display text-red-300 hover:text-white transition-colors truncate"
        data-testid="chat-contest-chip-link"
        title="Open the contest pitch page in a new tab"
      >
        <span className="text-red-500">//_BUILDERS_CONTEST</span>{" "}
        <span className="hidden sm:inline">·</span>{" "}
        <span>BACK DRR — {supportersLabel} </span>
        <span className="underline underline-offset-2">SEE PITCH →</span>
      </a>
      <a
        href="https://app.emergent.sh/showcase/fabrizio/06167798-e2fe-4861-b95d-691f145daee1"
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 border border-red-600 bg-red-600 text-black hover:bg-white transition-colors text-[10px] tracking-[0.2em] uppercase font-display"
        data-testid="chat-contest-chip-root-btn"
        aria-label="Vote for DRR on Emergent"
      >
        <Zap className="w-3 h-3" strokeWidth={2.5} />
        VOTE →
      </a>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem(CHIP_STORAGE_KEY, "1");
          setHidden(true);
        }}
        aria-label="Dismiss contest chip"
        className="shrink-0 p-1 text-zinc-500 hover:text-red-400 transition-colors"
        data-testid="chat-contest-chip-dismiss"
      >
        <X className="w-3 h-3" strokeWidth={2.5} />
      </button>
    </div>
  );
}

